import * as React from 'react'
import { cn } from '../../lib/utils'

export interface VorschlagsEintrag {
  key: string
  /** Mengenprobe bestanden und eindeutig zugeordnet — sonst nicht vorgewählt. */
  vorgewaehlt: boolean
  hinweise: string[]
}

export interface VorschlagsKastenTexte {
  /** „Vorschlag" */
  titel: string
  /** „Übernehmen" (je Eintrag) */
  uebernehmen: string
  /** „Markierte übernehmen" */
  markierte: string
  /** „Verwerfen" */
  verwerfen: string
  /** „Kein Eintrag erkannt." */
  leer: string
  /** Text eines Hinweises am Eintrag (`NUMBER_NOT_IN_TEXT` → „nicht im Text — bitte prüfen"). */
  hinweis: (code: string) => string
}

export interface VorschlagsKastenProps<T extends VorschlagsEintrag> {
  eintraege: T[]
  /** Wie ein Eintrag aussieht, liefert die Art. */
  darstellung: (e: T) => React.ReactNode
  /** Übernimmt die genannten Einträge in den Entwurf — ergänzt, ersetzt nie. Danach verschwinden sie aus dem Kasten. */
  onUebernehmen: (eintraege: T[]) => Promise<void> | void
  onVerwerfen: () => void
  texte: VorschlagsKastenTexte
  className?: string
}

/**
 * AP-46 21.7 — **Vorschlagskasten** für ERP und PWA: jeden Eintrag einzeln übernehmen oder weglassen, dazu
 * „Markierte übernehmen" und „Verwerfen". Nicht vorgewählt ist, was die Mengenprobe nicht bestanden hat oder
 * nicht eindeutig zugeordnet ist — die Hinweise stehen am Eintrag.
 */
export function VorschlagsKasten<T extends VorschlagsEintrag>({ eintraege, darstellung, onUebernehmen, onVerwerfen, texte, className }: VorschlagsKastenProps<T>) {
  const [offen, setOffen] = React.useState<T[]>(eintraege)
  const [markiert, setMarkiert] = React.useState<Set<string>>(() => new Set(eintraege.filter((e) => e.vorgewaehlt).map((e) => e.key)))
  const [laeuft, setLaeuft] = React.useState(false)
  React.useEffect(() => {
    setOffen(eintraege)
    setMarkiert(new Set(eintraege.filter((e) => e.vorgewaehlt).map((e) => e.key)))
  }, [eintraege])

  const nimm = async (liste: T[]) => {
    if (!liste.length || laeuft) return
    setLaeuft(true)
    try {
      await onUebernehmen(liste)
      const weg = new Set(liste.map((e) => e.key))
      setOffen((alt) => alt.filter((e) => !weg.has(e.key)))
    } finally {
      setLaeuft(false)
    }
  }

  const umschalten = (key: string, an: boolean) =>
    setMarkiert((alt) => {
      const neu = new Set(alt)
      if (an) neu.add(key)
      else neu.delete(key)
      return neu
    })

  return (
    <section className={cn('space-y-2 rounded-md border border-primary/40 bg-primary/5 p-3', className)} aria-label={texte.titel}>
      <h3 className="text-sm font-semibold">{texte.titel}</h3>
      {offen.length === 0 ? (
        <p className="text-sm text-muted-foreground">{texte.leer}</p>
      ) : (
        <ul className="space-y-2">
          {offen.map((e) => (
            <li key={e.key} className="flex items-start gap-2 rounded-md bg-background p-2">
              <input type="checkbox" aria-label={texte.uebernehmen} className="mt-1 h-5 w-5" checked={markiert.has(e.key)} onChange={(ev) => umschalten(e.key, ev.target.checked)} />
              <div className="min-w-0 flex-1 text-sm">
                {darstellung(e)}
                {e.hinweise.length > 0 && (
                  <ul className="mt-1 flex flex-wrap gap-1">
                    {e.hinweise.map((h) => (
                      <li key={h} className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                        {texte.hinweis(h)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button type="button" disabled={laeuft} onClick={() => void nimm([e])} className="shrink-0 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50">
                {texte.uebernehmen}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        {offen.length > 0 && (
          <button
            type="button"
            disabled={laeuft || !offen.some((e) => markiert.has(e.key))}
            onClick={() => void nimm(offen.filter((e) => markiert.has(e.key)))}
            className="inline-flex min-h-[40px] items-center rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {texte.markierte}
          </button>
        )}
        <button type="button" onClick={onVerwerfen} className="inline-flex min-h-[40px] items-center rounded-md border border-border px-3 py-2 text-sm hover:bg-muted">
          {texte.verwerfen}
        </button>
      </div>
    </section>
  )
}
